/**
 * Mock data for Training Module
 * Generates training category and item data
 */

import { generateId } from '../../mock/mockData';
import type { TrainingCategory, TrainingItem } from '../../../types/training';

// Generate category ID like TRN-001
export const generateCategoryId = (sequenceNumber: number): string => {
  const num = String(sequenceNumber).padStart(3, '0');
  return `TRN-${num}`;
};

// Generate item ID like TRN-001-001
export const generateItemId = (categoryId: string, itemSequence: number): string => {
  const num = String(itemSequence).padStart(3, '0');
  return `${categoryId}-${num}`;
};

const createCategory = (category: TrainingCategory): TrainingCategory => category;

// Mock training items (flat list, will be grouped by category)
export const mockTrainingItems: TrainingItem[] = [];

// Mock training categories with items
export const mockTrainingCategories: TrainingCategory[] = [
  createCategory({
    id: generateId(),
    categoryId: generateCategoryId(1),
    name: 'Getting Started',
    audience: 'Asset',
    order: 1,
    items: [
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(1), 1),
        title: 'Welcome to MiSpy.ai',
        description: 'Learn the basics of using MiSpy.ai platform and how to get started with your first mission.',
        link: 'https://help.mispy.ai/getting-started',
        categoryId: generateCategoryId(1),
        hidden: false,
        order: 1,
        createdAt: new Date('2024-01-15'),
        createdAtText: '2024-01-15',
        updatedAt: new Date('2024-01-15'),
        updatedAtText: '2024-01-15',
      },
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(1), 2),
        title: 'Creating Your Account',
        description: 'Step-by-step guide to create and verify your MiSpy.ai account.',
        link: 'https://help.mispy.ai/account-creation',
        categoryId: generateCategoryId(1),
        hidden: false,
        order: 2,
        createdAt: new Date('2024-01-15'),
        createdAtText: '2024-01-15',
        updatedAt: new Date('2024-01-15'),
        updatedAtText: '2024-01-15',
      },
    ],
    createdAt: new Date('2024-01-15'),
    createdAtText: '2024-01-15',
    updatedAt: new Date('2024-01-15'),
    updatedAtText: '2024-01-15',
  }),
  createCategory({
    id: generateId(),
    categoryId: generateCategoryId(2),
    name: 'Mission Creation',
    audience: 'Asset',
    order: 2,
    items: [
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(2), 1),
        title: 'How to Create a Mission',
        description: 'Step-by-step guide on creating different types of missions and understanding mission requirements.',
        link: 'https://help.mispy.ai/mission-creation',
        categoryId: generateCategoryId(2),
        hidden: false,
        order: 1,
        createdAt: new Date('2024-01-16'),
        createdAtText: '2024-01-16',
        updatedAt: new Date('2024-01-16'),
        updatedAtText: '2024-01-16',
      },
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(2), 2),
        title: 'Mission Types Explained',
        description: 'Understanding the differences between Snap-and-Grab, Surveillance, and Investigative missions.',
        link: 'https://help.mispy.ai/mission-types',
        categoryId: generateCategoryId(2),
        hidden: false,
        order: 2,
        createdAt: new Date('2024-01-16'),
        createdAtText: '2024-01-16',
        updatedAt: new Date('2024-01-16'),
        updatedAtText: '2024-01-16',
      },
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(2), 3),
        title: 'Using AI Assistant Pie',
        description: 'Learn how to use Pie, your AI assistant, to help create missions quickly.',
        link: 'https://help.mispy.ai/ai-assistant',
        categoryId: generateCategoryId(2),
        hidden: false,
        order: 3,
        createdAt: new Date('2024-01-16'),
        createdAtText: '2024-01-16',
        updatedAt: new Date('2024-01-16'),
        updatedAtText: '2024-01-16',
      },
    ],
    createdAt: new Date('2024-01-16'),
    createdAtText: '2024-01-16',
    updatedAt: new Date('2024-01-16'),
    updatedAtText: '2024-01-16',
  }),
  createCategory({
    id: generateId(),
    categoryId: generateCategoryId(3),
    name: 'Field Readiness',
    audience: 'Ghost',
    order: 3,
    items: [
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(3), 1),
        title: 'Accepting Your First Mission',
        description: 'Learn how to evaluate mission fit, confirm availability, and prepare for first contact.',
        link: 'https://help.mispy.ai/ghost-first-mission',
        categoryId: generateCategoryId(3),
        hidden: false,
        order: 1,
        createdAt: new Date('2024-01-17'),
        createdAtText: '2024-01-17',
        updatedAt: new Date('2024-01-17'),
        updatedAtText: '2024-01-17',
      },
    ],
    createdAt: new Date('2024-01-17'),
    createdAtText: '2024-01-17',
    updatedAt: new Date('2024-01-17'),
    updatedAtText: '2024-01-17',
  }),
  createCategory({
    id: generateId(),
    categoryId: generateCategoryId(4),
    name: 'Evidence Handling',
    audience: 'Ghost',
    order: 4,
    items: [
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(4), 1),
        title: 'Evidence Packaging Guide',
        description: 'Best practices for capturing, labeling, and packaging deliverables for admin review.',
        link: 'https://help.mispy.ai/evidence-packaging',
        categoryId: generateCategoryId(4),
        hidden: false,
        order: 1,
        createdAt: new Date('2024-01-18'),
        createdAtText: '2024-01-18',
        updatedAt: new Date('2024-01-18'),
        updatedAtText: '2024-01-18',
      },
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(4), 2),
        title: 'Chain of Custody Checklist',
        description: 'A checklist to keep evidence organized and defensible from pickup through delivery.',
        link: 'https://help.mispy.ai/chain-of-custody',
        categoryId: generateCategoryId(4),
        hidden: false,
        order: 2,
        createdAt: new Date('2024-01-18'),
        createdAtText: '2024-01-18',
        updatedAt: new Date('2024-01-18'),
        updatedAtText: '2024-01-18',
      },
    ],
    createdAt: new Date('2024-01-18'),
    createdAtText: '2024-01-18',
    updatedAt: new Date('2024-01-18'),
    updatedAtText: '2024-01-18',
  }),
  createCategory({
    id: generateId(),
    categoryId: generateCategoryId(5),
    name: 'Payouts & Compliance',
    audience: 'Ghost',
    order: 5,
    items: [
      {
        id: generateId(),
        itemId: generateItemId(generateCategoryId(5), 1),
        title: 'Payout Readiness',
        description: 'Understand payout setup, verification blockers, and common compliance issues for Ghost accounts.',
        link: 'https://help.mispy.ai/ghost-payouts',
        categoryId: generateCategoryId(5),
        hidden: false,
        order: 1,
        createdAt: new Date('2024-01-19'),
        createdAtText: '2024-01-19',
        updatedAt: new Date('2024-01-19'),
        updatedAtText: '2024-01-19',
      },
    ],
    createdAt: new Date('2024-01-19'),
    createdAtText: '2024-01-19',
    updatedAt: new Date('2024-01-19'),
    updatedAtText: '2024-01-19',
  }),
];

// Flatten items for easier access
mockTrainingCategories.forEach(category => {
  if (category.items) {
    mockTrainingItems.push(...category.items);
  }
});
